import { useState, useEffect, useRef, useCallback } from 'react';
import { skillService } from '@/services/skill-service';
import type { CreateValidTaskResponse } from '@/services/skill-service/types';
import i18n from '@/i18n/config';

// 定义队列中每个任务的结构
export type ValidationQueueItem = {
  skills_id?: string;
  key?: string;
  status?: 'queue' | 'running' | 'success' | 'failed';
  message?: string;
};

// Hook 的参数类型
interface UseSkillAsyncValidProps {
  pollingInterval?: number; // 轮询间隔，默认为 3000ms
  onValidSuccess?: (result: ValidationQueueItem[]) => void;
  onValidError?: (result: ValidationQueueItem[]) => void;
}

/**
 * 用于异步校验技能文件的 Hook
 * 1. 创建校验任务
 * 2. 轮询校验状态
 * 3. 返回成功或失败结果
 */
export const useSkillAsyncValid = ({
  pollingInterval = 2000,
  onValidSuccess,
  onValidError,
}: UseSkillAsyncValidProps = {}) => {
  const [queue, setQueue] = useState<ValidationQueueItem[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 更新队列中特定任务的状态
  const updateQueueItem = useCallback(
    (id: string, updates: Partial<ValidationQueueItem>) => {
      setQueue((prevQueue) =>
        prevQueue.map((item) =>
          item.skills_id === id ? { ...item, ...updates } : item,
        ),
      );
    },
    [],
  );

  // 从队列中移除任务
  const onDelete = useCallback((skills_id: string) => {
    setQueue((prevQueue) =>
      prevQueue.filter((item) => item.skills_id !== skills_id),
    );
  }, []);

  // 轮询逻辑
  const pollStatus = useCallback(async () => {
    const itemsToPoll = queue.filter(
      (item) => item.status !== 'success' && item.status !== 'failed',
    );

    if (itemsToPoll.length === 0) {
      return;
    }

    const skillIds = itemsToPoll.map((item) => item.skills_id!);

    try {
      const results: ValidationQueueItem[] =
        await skillService.getValidateStatus({ skills_ids: skillIds });

      const successfulItems: ValidationQueueItem[] = [];
      const failedItems: ValidationQueueItem[] = [];

      results.forEach((result: ValidationQueueItem) => {
        const itemInQueue: ValidationQueueItem = itemsToPoll.find(
          (i) => i.skills_id === result.skills_id,
        );
        if (!itemInQueue) return;

        // 更新队列中任务的最新状态
        updateQueueItem(result.skills_id, {
          status: result.status,
          message: result.message,
        });

        if (result.status === 'success') {
          successfulItems.push({ ...itemInQueue, ...result });
        } else if (result.status === 'failed') {
          failedItems.push({ ...itemInQueue, ...result });
        }
      });

      if (successfulItems.length > 0) {
        onValidSuccess?.(successfulItems);
      }

      if (failedItems.length > 0) {
        onValidError?.(failedItems);
      }
    } catch (error: any) {
      const errorItems = itemsToPoll.map((item) => ({
        ...item,
        status: 'failed' as const,
        message: i18n.t('common:error.pollValidationFailed'),
      }));

      errorItems.forEach((item) => {
        updateQueueItem(item.skills_id, {
          status: 'failed',
          message: item.message,
        });
      });

      onValidError?.(errorItems);
    }
  }, [queue, updateQueueItem, onValidSuccess, onValidError]);

  // 使用 effect 启动和停止轮询
  useEffect(() => {
    // 只要队列中存在需要轮询的项，就启动定时器
    const shouldPoll = queue.some(
      (item) => item.status !== 'success' && item.status !== 'failed',
    );

    if (shouldPoll) {
      // 立即执行一次
      pollStatus();

      // 设置定时器用于后续轮询
      if (!timerRef.current) {
        timerRef.current = setInterval(pollStatus, pollingInterval);
      }
    } else if (!shouldPoll && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [queue, pollingInterval, pollStatus]);

  // 对外暴露的方法，接收待校验的技能列表
  const onAddToQueue = useCallback(
    async (keys: string[]) => {
      try {
        const newTasks = await skillService.createValidateSkillsTask({
          object_keys: keys,
        });
        // 确保返回的每个任务都有一个初始状态，以便轮询
        const itemsToQueue: CreateValidTaskResponse = newTasks.map(
          (task: CreateValidTaskResponse[0]) => ({
            ...task,
            key: task.object_key,
            status: task.status || 'queue', // 后端可能不立即返回状态，设置一个默认值
          }),
        );
        setQueue((prevQueue) => [...prevQueue, ...itemsToQueue]);
      } catch (error) {
        console.error('创建验证失败', error);
      }
    },
    [setQueue],
  );

  const onRetry = useCallback(
    (key: string) => {
      // 先校验queue里有没有
      const item = queue.find((item) => item.key === key);
      if (!item) return;
      updateQueueItem(item.skills_id, {
        status: 'queue',
        message: i18n.t('common:validating'),
      });
      onAddToQueue([key]);
    },
    [queue, updateQueueItem, onAddToQueue],
  );

  const onClear = useCallback(() => {
    setQueue([]);
  }, []);

  return {
    queue,
    onAddToQueue,
    onDelete,
    onRetry,
    onClear,
  };
};
